import { useState, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2 } from "lucide-react";

import { ProjectVisibility } from "@optidev/shared-types";
import { VisibilitySelector } from "./VisibilitySelector";

interface PublishRequiredModalProps {
  isOpen: boolean;
  currentVisibility?: ProjectVisibility;
  onClose: () => void;
  onPublish: (visibility: ProjectVisibility) => void;
  isUpdating?: boolean;
}

export function PublishRequiredModal({
  isOpen,
  currentVisibility,
  onClose,
  onPublish,
  isUpdating = false,
}: PublishRequiredModalProps) {
  const [selectedVisibility, setSelectedVisibility] =
    useState<ProjectVisibility>(currentVisibility || ProjectVisibility.PUBLIC);

  // Sync with store when modal opens or currentVisibility changes
  useEffect(() => {
    if (currentVisibility) {
      setSelectedVisibility(currentVisibility);
    }
  }, [currentVisibility]);

  const handlePublish = () => {
    onPublish(selectedVisibility);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] w-full max-w-[90vw] rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            Publish Required
          </DialogTitle>
          <DialogDescription>
            Your project needs to be published before you can send it to OptiSigns.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Choose who can access your published content:
          </p>

          <VisibilitySelector
            value={selectedVisibility}
            onChange={setSelectedVisibility}
            disabled={isUpdating}
            isOpen={isOpen}
            onClose={onClose}
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isUpdating}>
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                "Publish Now"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
